import React, { useEffect, useState } from 'react';
import rrService from '../services/rrService';
import SortableResizableTable from '../components/SortableResizableTable';
import RebalancePanel from '../components/RebalancePanel';
import Select from 'react-select';
import '../styles/SelectStyles.css';
import { useRebalanceState } from '../hooks/useRebalanceState';
import {
  getReportColumnsWithFormatting,
  reportOptions,
  formatCurrency as baseCurrencyFormatter,
  getTradeActionStyles as baseTradeActionStyles
} from '../config/tableConfig';

const Rebalance = () => {
  // Use the custom hook to manage all state
  const {
    // File and group selection
    rebalancingGroups, setRebalancingGroups,
    selectedGroup, setSelectedGroup,
    groupHighLevelInfo, setGroupHighLevelInfo,

    // Loading states
    isLoading, setIsLoading,
    isLoadingHighLevelInfo, setIsLoadingHighLevelInfo,
    isLoadingReport, setIsLoadingReport,
    isLoadingCashActions, setIsLoadingCashActions,
    isLoadingSecurities, setIsLoadingSecurities,
    isLoadingTradeRestrictions, setIsLoadingTradeRestrictions,

    // Error states
    error, setError,
    reportError, setReportError,
    cashActionsError, setCashActionsError,
    securitiesError, setSecuritiesError,
    tradeRestrictionsError, setTradeRestrictionsError,

    // UI collapse states
    isHighLevelCollapsed, setIsHighLevelCollapsed,
    isCashActionCollapsed, setIsCashActionCollapsed,
    isCashActionGuideCollapsed, setIsCashActionGuideCollapsed,
    isSecuritiesInfoCollapsed, setIsSecuritiesInfoCollapsed,
    isSecuritiesInfoGuideCollapsed, setIsSecuritiesInfoGuideCollapsed,
    isTradeRestrictionsCollapsed, setIsTradeRestrictionsCollapsed,
    isTradeRestrictionsGuideCollapsed, setIsTradeRestrictionsGuideCollapsed,
    isDetailedCollapsed, setIsDetailedCollapsed,
    isRebalanceCollapsed, setIsRebalanceCollapsed, // <-- ADD THIS

    // Report data
    selectedReport, setSelectedReport,
    reportData, setReportData,
    cashActionsData, setCashActionsData,
    securitiesData, setSecuritiesData,
    tradeRestrictionsData, setTradeRestrictionsData,

    // Display preferences
    hideDollarValues, setHideDollarValues,
  } = useRebalanceState();

  const [hasLoadedHighLevelInfo, setHasLoadedHighLevelInfo] = useState(false);
  const [hasLoadedCashActions, setHasLoadedCashActions] = useState(false);
  const [hasLoadedSecurities, setHasLoadedSecurities] = useState(false);
  const [hasLoadedTradeRestrictions, setHasLoadedTradeRestrictions] = useState(false);
  const [hasLoadedReport, setHasLoadedReport] = useState(false);

  useEffect(() => {
    // Start loading rebalancing groups immediately on mount
    setIsLoading(true);
    setError('');
    fetchRebalancingGroups()
      .finally(() => setIsLoading(false));
  }, []);

  const fetchRebalancingGroups = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await rrService.getRebalancingGroups();

      if (data && data.groups && Array.isArray(data.groups)) {
        setRebalancingGroups(data.groups);

        // Set the first group as selected by default if no group is selected
        if (data.groups.length > 0 && !selectedGroup) {
          const firstGroup = data.groups[0].name;
          setSelectedGroup(firstGroup);
          // Load data for the first group
          fetchGroupHighLevelInfo(firstGroup);
        } else if (data.groups.length === 0) {
          console.warn('No groups found in the file');
          setError('No rebalancing groups found in the selected file.');
          setGroupHighLevelInfo(null);
        }
      } else {
        console.warn('Invalid groups data received:', data);
        setRebalancingGroups([]);
        setError('No rebalancing groups found in the selected file.');
        setGroupHighLevelInfo(null);
      }
    } catch (err) {
      console.error('Error in fetchRebalancingGroups:', err);
      setError('Error loading rebalancing groups: ' + (err.message || 'Unknown error')); setRebalancingGroups([]);
      setGroupHighLevelInfo(null);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGroupHighLevelInfo = async (groupName) => {
    if (!groupName) return;

    try {
      setIsLoadingHighLevelInfo(true);
      setError('');
      // Clear previous data immediately to prevent appending
      setGroupHighLevelInfo(null);
      
      const data = await rrService.getGroupHighLevelInfo(groupName, 'accountHoldings.parquet');

      if (data && data.sleeveData) {
        if (data.sleeveData && !Array.isArray(data.sleeveData)) {
          console.error('Sleeve data is not an array:', typeof data.sleeveData);
          // Convert to array if it's an object with numeric keys
          if (typeof data.sleeveData === 'object') {
            const sleeveArray = Object.values(data.sleeveData);
            if (sleeveArray.length > 0) {
              data.sleeveData = sleeveArray;
            }
          }
        }
      } else {
        console.error('No sleeve data in response:', data);
      }

      if (!data) {
        throw new Error('No high-level information returned from server');
      }

      setGroupHighLevelInfo(data);
    } catch (err) {
      console.error('Error fetching group high-level info:', err);
      setError('Error loading group high-level info: ' + (err.message || 'Unknown error'));
      setGroupHighLevelInfo(null);
    } finally {
      setIsLoadingHighLevelInfo(false);
    }
  };
  
  const handleGroupChange = (selectedOption) => {
    const newGroupName = selectedOption ? selectedOption.value : '';
    setSelectedGroup(newGroupName);
    setError(''); // Clear any previous errors
    
    // Clear all previous data immediately when changing groups
    setGroupHighLevelInfo(null);
    setCashActionsData(null);
    setSecuritiesData(null);
    setTradeRestrictionsData(null);
    setReportData(null);

    if (newGroupName) {
      fetchGroupHighLevelInfo(newGroupName);
    } else {
      if (!newGroupName) {
        console.warn('No group name selected');
      }
    }
  };
  // Format currency values for display (wraps the base formatter with hideDollarValues support)
  const formatCurrency = (value) => {
    return baseCurrencyFormatter(value, hideDollarValues);
  };

  // Custom styling function for trade action badges (use base styles)
  const getTradeActionStyles = baseTradeActionStyles;

  // Get column definitions with proper formatting context
  const getColumnsForReport = (reportType) => {
    return getReportColumnsWithFormatting(reportType, hideDollarValues);
  };

  // Generate specific column arrays that update when hideDollarValues changes
  const accountReportColumns = getColumnsForReport('accounts');
  const securitiesReportColumns = getColumnsForReport('securities');
  const cashActionsReportColumns = getColumnsForReport('cashActions');
  const tradeRestrictionsReportColumns = getColumnsForReport('tradeRestrictions');
  const sleeveTableColumns = getColumnsForReport('sleeves');
  // Function to fetch cash actions data separately
  const fetchCashActionsData = async () => {
    if (!selectedGroup) return;

    setIsLoadingCashActions(true);
    setCashActionsError('');
    // Clear previous data immediately to prevent appending
    setCashActionsData(null);

    try {
      const response = await rrService.getGroupCashActionsReport(selectedGroup);

      // Handle the response data generically
      let data = extractDataArray(response);

      // Normalize data to ensure consistency in field names
      data = normalizeReportData(data, 'cashActions');

      setCashActionsData(data);
    } catch (err) {
      console.error('Error fetching cash actions data:', err);
      setCashActionsError(`Error loading cash actions: ${err.message || 'Unknown error'}`);
      setCashActionsData(null);
    } finally {
      setIsLoadingCashActions(false);
    }
  };
  // Function to fetch securities data separately
  const fetchSecuritiesData = async () => {
    if (!selectedGroup) return;

    setIsLoadingSecurities(true);
    setSecuritiesError('');
    // Clear previous data immediately to prevent appending
    setSecuritiesData(null);

    try {

      const response = await rrService.getGroupBySecuritiesReport(selectedGroup);
      // Handle the response data generically
      let data = extractDataArray(response);

      // Normalize data to ensure consistency in field names
      data = normalizeReportData(data, 'securities');

      setSecuritiesData(data);
    } catch (err) {
      console.error('Error fetching securities data:', err);
      setSecuritiesError(`Error loading securities: ${err.message || 'Unknown error'}`);
      setSecuritiesData(null);
    } finally {
      setIsLoadingSecurities(false);
    }
  };

  // Function to fetch trade restrictions data separately
  const fetchTradeRestrictionsData = async () => {
    if (!selectedGroup) return;

    setIsLoadingTradeRestrictions(true);
    setTradeRestrictionsError('');
    // Clear previous data immediately to prevent appending
    setTradeRestrictionsData(null);

    try {

      const response = await rrService.getGroupTradeRestrictionsReport(selectedGroup);

      // Handle the response data generically
      let data = extractDataArray(response);

      // Normalize data to ensure consistency in field names
      data = normalizeReportData(data, 'tradeRestrictions');

      setTradeRestrictionsData(data);
    } catch (err) {
      console.error('Error fetching trade restrictions data:', err);
      setTradeRestrictionsError(`Error loading trade restrictions: ${err.message || 'Unknown error'}`);
      setTradeRestrictionsData(null);
    } finally {
      setIsLoadingTradeRestrictions(false);
    }
  };

  // Function to fetch report data based on selected type
  const fetchReportData = async (reportType) => {
    if (!selectedGroup) return;

    setIsLoadingReport(true);
    setReportError('');
    // Clear previous data immediately to prevent appending
    setReportData(null);

    try {

      let response;
      if (reportType === 'accounts') {
        response = await rrService.getGroupByAccountReport(selectedGroup);
      } else if (reportType === 'securities') {
        response = await rrService.getGroupBySecuritiesReport(selectedGroup);
      }

      // Handle the response data generically
      // Strategy: Look for an array in the response at any level
      let data = extractDataArray(response);

      // Normalize data to ensure consistency in field names
      data = normalizeReportData(data, reportType);

      setReportData(data);
    } catch (err) {
      console.error(`Error fetching ${reportType} report:`, err);
      setReportError(`Error loading ${reportType} report: ${err.message || 'Unknown error'}`);
      setReportData(null);
    } finally {
      setIsLoadingReport(false);
    }
  };

  // Helper function to extract an array from potentially nested response data
  const extractDataArray = (response) => {
    // If response is already an array, use it
    if (Array.isArray(response)) {
      return response;
    }

    // If response has a data property that's an array, use it
    if (response && response.data && Array.isArray(response.data)) {
      return response.data;
    }

    // If response is an object, search recursively for the first array
    if (response && typeof response === 'object') {

      // First check top-level properties
      for (const key in response) {
        if (Array.isArray(response[key])) {
          return response[key];
        }
      }

      // Then check one level deeper
      for (const key in response) {
        if (response[key] && typeof response[key] === 'object') {
          for (const nestedKey in response[key]) {
            if (Array.isArray(response[key][nestedKey])) {
              return response[key][nestedKey];
            }
          }
        }
      }
    }

    // Default to empty array if no array found
    return [];
  };
  // Helper function to normalize field names for consistency
  const normalizeReportData = (data, reportType) => {
    if (!data || data.length === 0) return data;

    return data.map(item => {
      const normalized = { ...item };

      // Map common field variations to standard names
      Object.keys(item).forEach(key => {
        const lowerKey = key.toLowerCase();

        // Account report specific normalizations
        if (reportType === 'accounts') {
          if (lowerKey.includes('account') && lowerKey.includes('name')) {
            normalized.accountName = item[key];
          }
          if (lowerKey.includes('account') && lowerKey.includes('number')) {
            normalized.accountNumber = item[key];
          }
        }

        // Securities report specific normalizations
        if (reportType === 'securities') {
          if (lowerKey === 'symbol' || lowerKey === 'ticker') {
            normalized.symbol = item[key];
          }
        }
        // Cash actions report specific normalizations
        if (reportType === 'cashActions') {
          if (lowerKey.includes('account') && lowerKey.includes('name')) {
            normalized['Account Name'] = item[key];
          }
          if (lowerKey.includes('account') && lowerKey.includes('number')) {
            normalized['Account Number'] = item[key];
          }
          if (lowerKey === 'cash action') {
            normalized['Cash Action'] = item[key];
          }
          if (lowerKey === 'holdings current value') {
            normalized['Holdings Current Value'] = item[key];
          }
          if (lowerKey === 'calculated reserve center') {
            normalized['Calculated Reserve Center'] = item[key];
          }
          if (lowerKey === 'calculated reserve min') {
            normalized['Calculated Reserve Min'] = item[key];
          }
          if (lowerKey === 'calculated reserve max') {
            normalized['Calculated Reserve Max'] = item[key];
          }
          // Keep legacy mappings for backward compatibility
          if (lowerKey.includes('cash') && lowerKey.includes('action') && lowerKey.includes('type')) {
            normalized['Cash Action Type'] = item[key];
          }
          if (lowerKey.includes('cash') && lowerKey.includes('amount')) {
            normalized['Cash Amount'] = item[key];
          }
          if (lowerKey.includes('cash') && lowerKey.includes('direction')) {
            normalized['Cash Direction'] = item[key];
          }
          if (lowerKey.includes('transaction') && lowerKey.includes('date')) {
            normalized['Transaction Date'] = item[key];
          }
          if (lowerKey.includes('cash') && lowerKey.includes('status')) {
            normalized['Cash Status'] = item[key];
          }
          if (lowerKey.includes('cash') && lowerKey.includes('note')) {
            normalized['Cash Notes'] = item[key];
          }
        }

        // Common field normalizations for both report types
        if (lowerKey.includes('sleeve') && lowerKey.includes('name')) {
          normalized.sleeveName = item[key];
        }
        if (lowerKey.includes('holdings') && lowerKey.includes('value')) {
          normalized.holdingsCurrentValue = item[key];
        }
        if (lowerKey.includes('target') && lowerKey.includes('weight')) {
          normalized.targetWeight = item[key];
        }
        if (lowerKey.includes('actual') && lowerKey.includes('weight') && !lowerKey.includes('assigned')) {
          normalized.actualWeight = item[key];
        }
        if (lowerKey.includes('actual') && lowerKey.includes('weight') && lowerKey.includes('assigned')) {
          normalized.actualWeightOfAssigned = item[key];
        }
      });

      return normalized;
    });
  };

  // Update the handleReportChange function
  const handleReportChange = (selectedOption) => {
    const reportType = selectedOption ? selectedOption.value : 'accounts';
    setSelectedReport(reportType);
    fetchReportData(reportType);
  };  // Reset hasLoaded flags when group or file changes
  useEffect(() => {
    setHasLoadedHighLevelInfo(false);
    setHasLoadedCashActions(false);
    setHasLoadedSecurities(false);
    setHasLoadedTradeRestrictions(false);
    setHasLoadedReport(false);

    // Trigger API calls for expanded panels when group/file changes
    // Only fetch if we don't already have data for the new group/file
    if (selectedGroup) {
      if (!isHighLevelCollapsed && !groupHighLevelInfo) {
        setIsLoadingHighLevelInfo(true);
        fetchGroupHighLevelInfo(selectedGroup).finally(() => {
          setHasLoadedHighLevelInfo(true);
        });
      }
      if (!isCashActionCollapsed && !cashActionsData) {
        setIsLoadingCashActions(true);
        fetchCashActionsData().finally(() => {
          setHasLoadedCashActions(true);
        });
      }
      if (!isSecuritiesInfoCollapsed && !securitiesData) {
        setIsLoadingSecurities(true);
        fetchSecuritiesData().finally(() => {
          setHasLoadedSecurities(true);
        });
      }
      if (!isTradeRestrictionsCollapsed && !tradeRestrictionsData) {
        setIsLoadingTradeRestrictions(true);
        fetchTradeRestrictionsData().finally(() => {
          setHasLoadedTradeRestrictions(true);
        });
      }
      if (!isDetailedCollapsed && !reportData) {
        setIsLoadingReport(true);
        fetchReportData(selectedReport).finally(() => {
          setHasLoadedReport(true);
        });
      }
    }
  }, [selectedGroup]);

  // Panel expand handlers for lazy loading
  const handleExpandHighLevelInfo = () => {
    setIsHighLevelCollapsed((prev) => {
      const next = !prev;
      if (!next && !hasLoadedHighLevelInfo && selectedGroup) {
        setIsLoadingHighLevelInfo(true);
        fetchGroupHighLevelInfo(selectedGroup, true).finally(() => {
          setHasLoadedHighLevelInfo(true);
        });
      }
      return next;
    });
  };
  const handleExpandCashAction = () => {
    setIsCashActionCollapsed((prev) => {
      const next = !prev;
      if (!next && !hasLoadedCashActions && selectedGroup) {
        setIsLoadingCashActions(true);
        fetchCashActionsData().finally(() => {
          setHasLoadedCashActions(true);
        });
      }
      return next;
    });
  };
  const handleExpandSecuritiesInfo = () => {
    setIsSecuritiesInfoCollapsed((prev) => {
      const next = !prev;
      if (!next && !hasLoadedSecurities && selectedGroup) {
        setIsLoadingSecurities(true);
        fetchSecuritiesData().finally(() => {
          setHasLoadedSecurities(true);
        });
      }
      return next;
    });
  };
  const handleExpandTradeRestrictions = () => {
    setIsTradeRestrictionsCollapsed((prev) => {
      const next = !prev;
      if (!next && !hasLoadedTradeRestrictions && selectedGroup) {
        setIsLoadingTradeRestrictions(true);
        fetchTradeRestrictionsData().finally(() => {
          setHasLoadedTradeRestrictions(true);
        });
      }
      return next;
    });
  };

  // Add handler for Rebalance panel
  const handleExpandRebalance = () => {
    setIsRebalanceCollapsed((prev) => !prev);
  };

  const handleExpandDetailed = () => {
    setIsDetailedCollapsed((prev) => {
      const next = !prev;
      if (!next && !hasLoadedReport && selectedGroup) {
        setIsLoadingReport(true);
        fetchReportData(selectedReport).finally(() => {
          setHasLoadedReport(true);
        });
      }
      return next;
    });
  };

  useEffect(() => {
    if (
      !isHighLevelCollapsed &&
      !hasLoadedHighLevelInfo &&
      selectedGroup
    ) {
      setIsLoadingHighLevelInfo(true);
      fetchGroupHighLevelInfo(selectedGroup).finally(() => {
        setHasLoadedHighLevelInfo(true);
      });
    }
    // eslint-disable-next-line
  }, [isHighLevelCollapsed, selectedGroup]);

  useEffect(() => {
    if (
      !isCashActionCollapsed &&
      !hasLoadedCashActions &&
      selectedGroup
    ) {
      setIsLoadingCashActions(true);
      fetchCashActionsData().finally(() => {
        setHasLoadedCashActions(true);
      });
    }
    // eslint-disable-next-line
  }, [isCashActionCollapsed, selectedGroup]);

  useEffect(() => {
    if (
      !isSecuritiesInfoCollapsed &&
      !hasLoadedSecurities &&
      selectedGroup
    ) {
      setIsLoadingSecurities(true);
      fetchSecuritiesData().finally(() => {
        setHasLoadedSecurities(true);
      });
    }
    // eslint-disable-next-line
  }, [isSecuritiesInfoCollapsed, selectedGroup]);

  useEffect(() => {
    if (
      !isTradeRestrictionsCollapsed &&
      !hasLoadedTradeRestrictions &&
      selectedGroup
    ) {
      setIsLoadingTradeRestrictions(true);
      fetchTradeRestrictionsData().finally(() => {
        setHasLoadedTradeRestrictions(true);
      });
    }
    // eslint-disable-next-line
  }, [isTradeRestrictionsCollapsed, selectedGroup]);

  useEffect(() => {
    if (
      !isDetailedCollapsed &&
      !hasLoadedReport &&
      selectedGroup
    ) {
      setIsLoadingReport(true);
      fetchReportData(selectedReport).finally(() => {
        setHasLoadedReport(true);
      });
    }
    // eslint-disable-next-line
  }, [isDetailedCollapsed, selectedGroup, selectedReport]);

  // Function to enhance the SortableResizableTable component
  const getTableConfig = (data) => {
    if (!data || data.length === 0) {
      // Return appropriate columns based on report type
      if (selectedReport === 'accounts') {
        return { columns: accountReportColumns };
      } else if (selectedReport === 'securities') {
        return { columns: securitiesReportColumns };
      }
      // For cash actions, use the dedicated cash actions columns
      return { columns: cashActionsReportColumns };
    }

    // Create a function to format value display
    const renderCell = (value, item, columnKey) => {
      if (value === undefined || value === null) return '';
      // Special handling for Cash Action status
      if (columnKey === 'Cash Action') {
        let badgeClass = 'badge ';
        if (value === 'Raise Cash') {
          badgeClass += 'bg-danger'; // Red for raise cash (warning)
        } else if (value === 'Invest Cash') {
          badgeClass += 'bg-success'; // Green for invest cash (opportunity)
        } else {
          badgeClass += 'bg-secondary'; // Gray for neutral (no action needed)
        }
        return <span className={badgeClass} style={{ fontSize: '0.9rem', padding: '5px 8px' }} title={value}>{value}</span>;
      }      // Special handling for Trade Action status
      if (columnKey === 'Trade Action') {
        if (!value || value === '') {
          return <span className="text-muted" style={{ fontSize: '0.9rem' }}>-</span>;
        }

        let badgeClass = 'badge ';
        if (value === 'Buy') {
          badgeClass += 'bg-success'; // Green for buy action
        } else if (value === 'Sell') {
          badgeClass += 'bg-danger'; // Red for sell action
        } else if (value === 'Hold') {
          badgeClass += 'bg-secondary'; // Gray for hold action
        } else {
          badgeClass += 'bg-light text-dark'; // Light gray for unknown actions
        }
        return <span className={badgeClass} style={{ fontSize: '0.9rem', padding: '5px 8px' }} title={value}>{value}</span>;
      }      // Special handling for Trade Action With Restriction status
      if (columnKey === 'Trade Action With Restriction' || columnKey === 'Trade Action With Restriction (of Assigned)') {
        if (!value || value === '') {
          return <span className="text-muted" style={{ fontSize: '0.9rem' }}>-</span>;
        }

        // Convert to string and check for action keywords
        const actionText = String(value);
        const upperText = actionText.toUpperCase();

        let customClass = '';

        // Check for primary action keywords (BUY, SELL, HOLD)
        if (upperText === 'BUY') {
          // Pure BUY action
          customClass = 'trade-action-buy';
        } else if (upperText === 'SELL') {
          // Pure SELL action
          customClass = 'trade-action-sell';
        } else if (upperText === 'HOLD') {
          // HOLD action
          customClass = 'trade-action-hold';
        } else if (upperText.includes('BUY') && upperText.includes('WITHIN WEIGHT')) {
          // BUY with Weight restriction
          customClass = 'trade-action-buy-weight';
        } else if (upperText.includes('SELL') && upperText.includes('WITHIN WEIGHT')) {
          // SELL with Weight restriction
          customClass = 'trade-action-sell-weight';
        } else if (upperText.includes('BUT')) {
          // BUY/SELL but (...) - conflicting restriction
          customClass = 'trade-action-conflict';
        } else {
          customClass = 'trade-action-unknown';
        }

        const customStyle = {
          fontSize: '0.9rem',
          padding: '5px 8px',
          borderRadius: '4px',
          fontWeight: '500',
          display: 'inline-block',
          ...getTradeActionStyles(customClass)
        };

        return <span className={customClass} style={customStyle} title={actionText}>{actionText}</span>;
      }// Special handling for Restriction column - highlight BUY, SELL, HOLD within restriction text
      if (columnKey === 'Restriction') {
        if (!value || value === '') {
          return <span className="text-muted" style={{ fontSize: '0.9rem' }}>-</span>;
        }

        // Convert to string and check for action keywords
        const restrictionText = String(value);
        const upperText = restrictionText.toUpperCase();

        // Style based on restriction type - use the FIRST keyword found (client restrictions take precedence)
        let badgeClass = 'badge ';
        let displayText = restrictionText;

        // Check for keywords in order they appear in the text (first found wins)
        const buyIndex = upperText.indexOf('BUY');
        const sellIndex = upperText.indexOf('SELL');
        const holdIndex = upperText.indexOf('HOLD');
        const weightIndex = upperText.indexOf('WEIGHT');

        // Find which keyword appears first
        const positions = [
          { keyword: 'BUY', index: buyIndex, class: 'bg-success' },
          { keyword: 'SELL', index: sellIndex, class: 'bg-danger' },
          { keyword: 'HOLD', index: holdIndex, class: 'bg-secondary' },
          { keyword: 'WEIGHT', index: weightIndex, class: 'bg-info text-white' }
        ].filter(item => item.index !== -1).sort((a, b) => a.index - b.index);

        if (positions.length > 0) {
          badgeClass += positions[0].class; // Use the first keyword's color
        } else {
          // For other restrictions, use a neutral style
          badgeClass += 'bg-info text-white'; // Blue for general restrictions
        }

        return <span className={badgeClass} style={{ fontSize: '0.9rem', padding: '5px 8px' }} title={restrictionText}>{displayText}</span>;
      }// Special handling for Holdings Current Value (cash) in cash actions report
      if (columnKey === 'Holdings Current Value' && (data === cashActionsData || data.some(item => item['Cash Action']))) {
        const minReserve = item['Calculated Reserve Min'] || 0;
        const maxReserve = item['Calculated Reserve Max'] || 0;
        let textClass = '';
        const formattedValue = formatCurrency(value);
        let tooltipText = formattedValue;

        if (value < minReserve) {
          // Cash below minimum threshold - warning
          textClass = 'text-danger fw-bold';
          tooltipText += ` (Below minimum reserve: ${formatCurrency(minReserve)})`;
        } else if (value > maxReserve) {
          // Cash above maximum threshold - could be invested
          textClass = 'text-success fw-bold';
          tooltipText += ` (Above maximum reserve: ${formatCurrency(maxReserve)})`;
        } else {
          tooltipText += ` (Within reserve range: ${formatCurrency(minReserve)} - ${formatCurrency(maxReserve)})`;
        }

        return <span className={textClass} title={tooltipText}>{formattedValue}</span>;
      }

      // Color coding for weight comparison in Reports table
      if (item['Target Weight'] !== undefined &&
        (columnKey === 'Actual Weight' || columnKey === 'Actual Weight (of Assigned)')) {
        const threshold = 0.5; // 0.5% threshold to avoid rounding issues
        const targetWeight = item['Target Weight'];
        let difference;
        let tooltipText = '';
        let weightComparisonClass = '';

        if (columnKey === 'Actual Weight' && value !== undefined) {
          difference = value - targetWeight;
          if (difference > threshold) {
            weightComparisonClass = 'text-sell fw-bold'; // Green if actual weight is higher
            tooltipText = `Above target by ${difference.toFixed(2)}%`;

            // Format the output with the difference indicator
            return (
              <React.Fragment>
                <span className={weightComparisonClass} title={tooltipText}>
                  {typeof value === 'number' ? `${value.toFixed(2)}%` : value} <span>(+{difference.toFixed(2)}%)</span>
                </span>
              </React.Fragment>
            );
          } else if (difference < -threshold) {
            weightComparisonClass = 'text-buy fw-bold'; // Red if actual weight is lower
            tooltipText = `Below target by ${Math.abs(difference).toFixed(2)}%`;

            // Format the output with the difference indicator
            return (
              <React.Fragment>
                <span className={weightComparisonClass} title={tooltipText}>
                  {typeof value === 'number' ? `${value.toFixed(2)}%` : value} <span>({difference.toFixed(2)}%)</span>
                </span>
              </React.Fragment>
            );
          } else {
            // Within threshold - no color highlight but still show tooltip
            tooltipText = difference === 0 ?
              `Matches target (${targetWeight}%)` :
              (difference > 0 ?
                `Above target by ${difference.toFixed(2)}%` :
                `Below target by ${Math.abs(difference).toFixed(2)}%`);

            return (
              <React.Fragment>
                <span title={tooltipText}>
                  {typeof value === 'number' ? `${value.toFixed(2)}%` : value}
                </span>
              </React.Fragment>
            );
          }
        } else if (columnKey === 'Actual Weight (of Assigned)' && value !== undefined) {
          // Always calculate difference from target, but only highlight when outside min/max bounds
          const minTargetWeight = item['Minimum Target Weight'] || item['minimumTargetWeight'] || targetWeight;
          const maxTargetWeight = item['Maximum Target Weight'] || item['maximumTargetWeight'] || targetWeight;

          // Always calculate difference from target
          const difference = value - targetWeight;

          if (value > maxTargetWeight) {
            weightComparisonClass = 'text-sell fw-bold'; // Green if above maximum threshold
            tooltipText = `Above maximum target (${maxTargetWeight}%)`;

            return (
              <React.Fragment>
                <span className={weightComparisonClass} title={tooltipText}>
                  {typeof value === 'number' ? `${value.toFixed(2)}%` : value} <span>({difference > 0 ? '+' : ''}${difference.toFixed(2)}%)</span>
                </span>
              </React.Fragment>
            );
          } else if (value < minTargetWeight) {
            weightComparisonClass = 'text-buy fw-bold'; // Red if below minimum threshold
            tooltipText = `Below minimum target (${minTargetWeight}%)`;

            return (
              <React.Fragment>
                <span className={weightComparisonClass} title={tooltipText}>
                  {typeof value === 'number' ? `${value.toFixed(2)}%` : value} <span>({difference > 0 ? '+' : ''}${difference.toFixed(2)}%)</span>
                </span>
              </React.Fragment>
            );
          } else {
            // Within acceptable range - show difference from target without color coding
            tooltipText = `Within target range (${minTargetWeight}% - ${maxTargetWeight}%)`;

            return (
              <React.Fragment>
                <span title={tooltipText}>
                  {typeof value === 'number' ? `${value.toFixed(2)}%` : value} <span>({difference > 0 ? '+' : ''}${difference.toFixed(2)}%)</span>
                </span>
              </React.Fragment>
            );
          }
        }
      }

      // Format the value for display and add tooltip to show full value
      let displayValue;
      let tooltipText = '';
      // Handle currency formatting based on field name patterns
      if (typeof value === 'number') {
        tooltipText = value.toString();

        // First check if this is a weight/percentage column by the column key
        if (
          columnKey.toLowerCase().includes('weight') ||
          columnKey.toLowerCase().includes('percent') ||
          columnKey.toLowerCase().includes('allocation') ||
          columnKey.toLowerCase().includes('ratio')
        ) {
          tooltipText = `${value}%`;
          displayValue = `${value.toFixed(2)}%`;
        }
        // Then check for currency fields by column key
        else if (
          columnKey.toLowerCase().includes('value') ||
          columnKey.toLowerCase().includes('price') ||
          columnKey.toLowerCase().includes('goal') ||
          columnKey.toLowerCase().includes('dollar') ||
          columnKey.toLowerCase().includes('cost') ||
          columnKey.toLowerCase().includes('amount') ||
          columnKey.toLowerCase().includes('reserve')
        ) {
          tooltipText = formatCurrency(value);
          displayValue = formatCurrency(value);
        }
        // Format other numbers with appropriate decimal places (no fallback pattern matching)
        else if (!Number.isInteger(value)) {
          tooltipText = value.toString();
          displayValue = value.toFixed(2);
        }
        else {
          tooltipText = value.toString();
          displayValue = value;
        }
      }

      // Handle arrays (like account names or account numbers)
      else if (Array.isArray(value)) {
        tooltipText = value.join(', ');
        displayValue = value.join(', ');
      }

      // Handle objects by converting to a string representation
      else if (typeof value === 'object' && value !== null) {
        try {
          tooltipText = JSON.stringify(value);
          displayValue = JSON.stringify(value);
        } catch (e) {
          tooltipText = '[Object]';
          displayValue = '[Object]';
        }
      }

      // Handle strings and other types
      else {
        tooltipText = String(value);
        displayValue = value;
      }

      // Return value with tooltip for all cell types
      return <span title={tooltipText}>{displayValue}</span>;
    };

    // Return custom render function to handle various field formats
    return {
      customRender: {
        renderCell
      }
    };
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Rebalance Portfolio</h2>
        <div className="d-flex align-items-center gap-3">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="dollarToggle"
              checked={!hideDollarValues}
              onChange={(e) => setHideDollarValues(!e.target.checked)}
            />
            <label className="form-check-label" htmlFor="dollarToggle">
              Show Dollar Values
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
          <div className="mt-2">
            <small>If you're seeing connection issues, please ensure the RR Service is running.</small>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="d-flex justify-content-center my-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading data...</span>
        </div>
      )}

      <div className="card mt-4">
        <div className="card-body">
          <h5 className="card-title">Portfolio Selection</h5>

          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label">Rebalancing Group:</label>
              <Select
                className="basic-single"
                classNamePrefix="select"
                value={selectedGroup ? { value: selectedGroup, label: selectedGroup } : null}
                isDisabled={isLoading || rebalancingGroups.length === 0}
                isLoading={isLoading}
                isClearable={true}
                isSearchable={true}
                name="rebalancingGroup"
                options={rebalancingGroups.map(group => ({
                  value: group.name,
                  label: group.name
                }))}
                placeholder="Select a rebalancing group..."
                noOptionsMessage={() => "No rebalancing groups available"}
                onChange={handleGroupChange}
                menuPortalTarget={document.body}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isFocused ? '#80bdff' : '#ced4da',
                    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : null,
                    '&:hover': {
                      borderColor: state.isFocused ? '#80bdff' : '#adb5bd'
                    }
                  }),
                  menuPortal: (baseStyles) => ({
                    ...baseStyles,
                    zIndex: 10001
                  }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    zIndex: 10001
                  }),
                  option: (baseStyles, state) => ({
                    ...baseStyles,
                    backgroundColor: state.isSelected
                      ? '#007bff'
                      : state.isFocused
                        ? '#e9ecef'
                        : null,
                    color: state.isSelected ? 'white' : 'black',
                  })
                }}
              />
            </div>
          </div>

          {/* Group Data Table */}
          {selectedGroup ? (
            <div className="mt-4">
              {/* High-Level Information */}
              {groupHighLevelInfo && (
                <>
                  <div className="card mb-4">
                    <div
                      className="card-header bg-primary text-white d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={handleExpandHighLevelInfo}
                    >
                      <h5 className="mb-0">
                        <i className={`bi ${isHighLevelCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
                        {' '}High-Level Information
                      </h5>
                    </div>
                    <div className={`collapse ${!isHighLevelCollapsed ? 'show' : ''}`}>
                      <div className="card-body">
                        {isLoadingHighLevelInfo ? (
                          <div className="d-flex justify-content-center my-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading high-level info...</span>
                            </div>
                            <span className="ms-2">Loading high-level info...</span>
                          </div>
                        ) : (
                          <div className="row">
                            <div className="col-md-12">
                              <table className="table table-borderless" style={{ tableLayout: 'auto' }}>
                                <tbody>
                                  <tr>
                                    <th scope="row" style={{ width: '1%', whiteSpace: 'nowrap', paddingRight: '0.5rem' }}>Rebalancing Group Account Number:</th>
                                    <td style={{ width: 'auto', paddingRight: '2rem' }}>{groupHighLevelInfo.rebalancingGroupAccountNumber}</td>
                                    <th scope="row" style={{ width: '1%', whiteSpace: 'nowrap', paddingRight: '0.5rem' }}>Model:</th>
                                    <td style={{ width: '1%', whiteSpace: 'nowrap', paddingLeft: '0.5rem' }}>{groupHighLevelInfo.model}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row" style={{ width: 'auto', whiteSpace: 'nowrap' }}>Accounts:</th>
                                    <td colSpan="3">{Array.isArray(groupHighLevelInfo.accounts) ?
                                      groupHighLevelInfo.accounts.join(', ') :
                                      groupHighLevelInfo.accounts}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Summary Cards */}
                        <div className="row mb-4">
                          <div className="col-md-4">
                            <div className="card bg-light">
                              <div className="card-body text-center">
                                <h5 className="card-title">Total Holdings</h5>
                                <h3 className="card-text">{formatCurrency(groupHighLevelInfo.totalHoldingsCurrentValue)}</h3>
                                <p className="text-muted small">
                                  Assigned: {formatCurrency(groupHighLevelInfo.totalAssignedHoldingsCurrentValue)} (across {groupHighLevelInfo.accounts?.length || 0} accounts)
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="card bg-light">
                              <div className="card-body text-center">
                                <h5 className="card-title">Cash Reserve</h5>
                                <h3 className="card-text">{formatCurrency(groupHighLevelInfo.totalCashReserveGoalDollar)}</h3>
                                <p className="text-muted small">
                                  {(groupHighLevelInfo.totalCashReserveGoalDollar / groupHighLevelInfo.totalHoldingsCurrentValue * 100).toFixed(1)}% of total holdings
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="card bg-light">
                              <div className="card-body text-center">
                                <h5 className="card-title">Rebalancing Value</h5>
                                <h3 className="card-text">{formatCurrency(groupHighLevelInfo.totalRebalancingValue)}</h3>
                                <p className="text-muted small">
                                  Assigned: {formatCurrency(groupHighLevelInfo.totalAssignedRebalancingValue)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Sleeve Data Table with Visual Representation */}
                        {groupHighLevelInfo.sleeveData && Array.isArray(groupHighLevelInfo.sleeveData) && groupHighLevelInfo.sleeveData.length > 0 && (
                          <div className="mt-4">
                            {/* Visual representation of securities allocation */}
                            <div className="mb-3">
                              <h6 className="mb-2">Securities Allocation ({groupHighLevelInfo.sleeveData.length} securities)</h6>
                              <div className="progress" style={{ height: '30px' }}>
                                {[...groupHighLevelInfo.sleeveData]
                                  .sort((a, b) => b["Actual Weight"] - a["Actual Weight"])
                                  .map((sleeve, index) => {
                                    const colors = ['#007bff', '#28a745', '#fd7e14', '#6f42c1', '#20c997', '#e83e8c'];
                                    const color = colors[index % colors.length];

                                    return (
                                      <div
                                        key={index}
                                        className="progress-bar"
                                        role="progressbar"
                                        style={{
                                          width: `${sleeve["Actual Weight"]}%`,
                                          backgroundColor: color
                                        }}
                                        aria-valuenow={sleeve["Actual Weight"]}
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                        title={`${sleeve["Sleeve Name"]}: ${sleeve["Actual Weight"]}%`}
                                      >
                                        {sleeve["Actual Weight"] > 5 ? `${sleeve["Sleeve Name"]} ${sleeve["Actual Weight"]}%` : ''}
                                      </div>
                                    );
                                  })}
                              </div>
                              <div className="mt-2 d-flex flex-wrap">
                                {[...groupHighLevelInfo.sleeveData]
                                  .sort((a, b) => b["Actual Weight"] - a["Actual Weight"])
                                  .map((sleeve, index) => {
                                    const colors = ['#007bff', '#28a745', '#fd7e14', '#6f42c1', '#20c997', '#e83e8c'];
                                    const color = colors[index % colors.length];

                                    return (
                                      <div key={index} className="me-3 mb-1">
                                        <span className="badge" style={{ backgroundColor: color }}>&nbsp;</span>
                                        <span className="ms-1">{sleeve["Sleeve Name"]} ({sleeve["Actual Weight"]}%)</span>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                            <div>
                              <SortableResizableTable
                                columns={sleeveTableColumns}
                                data={[...groupHighLevelInfo.sleeveData].sort((a, b) => b["Actual Weight"] - a["Actual Weight"])}
                                hideDollarValues={hideDollarValues}
                                customRender={{
                                  accountNames: (value) => {
                                    const formattedValue = Array.isArray(value) ? value.join(', ') : String(value);
                                    return <span title={formattedValue}>{formattedValue}</span>;
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Cash Action Collapsible Panel */}
                  <div className="card mb-4">
                    <div
                      className="card-header bg-warning text-dark d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={handleExpandCashAction}
                    >
                      <h5 className="mb-0">
                        <i className={`bi ${isCashActionCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
                        {' '}Cash Action
                      </h5>
                    </div>
                    <div className={`collapse ${!isCashActionCollapsed ? 'show' : ''}`}>
                      <div className="card-body">                        {/* Cash Actions Guide - Collapsible */}
                        <div className="row mb-4">
                          <div className="col-md-12">
                            <div className="card h-100 bg-light">
                              <div
                                className="card-header bg-light d-flex justify-content-between align-items-center py-2"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setIsCashActionGuideCollapsed(!isCashActionGuideCollapsed)}
                              >
                                <h6 className="mb-0">
                                  <i className={`bi ${isCashActionGuideCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
                                  {' '}Cash Actions Guide
                                </h6>
                              </div>
                              <div className={`collapse ${!isCashActionGuideCollapsed ? 'show' : ''}`}>
                                <div className="card-body py-2">
                                  <div className="small">
                                    <p className="mb-2">This report shows the current cash position for each account and the recommended actions based on cash reserve thresholds.</p>
                                    <div className="d-flex flex-wrap gap-3">
                                      <div><span className="badge bg-danger">Raise Cash</span> - Below minimum threshold</div>
                                      <div><span className="badge bg-success">Invest Cash</span> - Above maximum threshold</div>
                                      <div><span className="badge bg-secondary">Neutral</span> - Within target range</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Cash Actions Loading State */}
                        {isLoadingCashActions ? (
                          <div className="d-flex justify-content-center my-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading cash actions...</span>
                            </div>
                            <span className="ms-2">Loading cash actions...</span>
                          </div>
                        ) : cashActionsError ? (
                          <div className="alert alert-danger">
                            <strong>Error:</strong> {cashActionsError}
                          </div>
                        ) : cashActionsData ? (
                          <div className="mt-4">
                            <SortableResizableTable
                              columns={cashActionsReportColumns}
                              data={cashActionsData}
                              hideDollarValues={hideDollarValues}
                              customRender={getTableConfig(cashActionsData).customRender}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* Trade Restrictions Panel */}
                  <div className="card mb-4">
                    <div
                      className="card-header bg-dark text-white d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={handleExpandTradeRestrictions}
                    >
                      <h5 className="mb-0">
                        <i className={`bi ${isTradeRestrictionsCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
                        {' '}Trade Restrictions
                      </h5>
                    </div>
                    <div className={`collapse ${!isTradeRestrictionsCollapsed ? 'show' : ''}`}>
                      <div className="card-body">
                        {/* Trade Restrictions Guide - Collapsible */}
                        <div className="row mb-4">
                          <div className="col-md-12">
                            <div className="card h-100 bg-light">
                              <div
                                className="card-header bg-light d-flex justify-content-between align-items-center py-2"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setIsTradeRestrictionsGuideCollapsed(!isTradeRestrictionsGuideCollapsed)}
                              >
                                <h6 className="mb-0">
                                  <i className={`bi ${isTradeRestrictionsGuideCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
                                  {' '}Trade Restrictions Guide
                                </h6>
                              </div>
                              <div className={`collapse ${!isTradeRestrictionsGuideCollapsed ? 'show' : ''}`}>
                                <div className="card-body py-2">
                                  <div className="small">
                                    <p className="mb-2">This report shows securities with trading restrictions, combining both Model and Client restrictions where applicable.</p>

                                    <h6 className="mb-2 text-primary">Restriction Types:</h6>
                                    <div className="d-flex flex-wrap gap-3 mb-3">
                                      <div><span className="badge bg-success">BUY ONLY</span> - BUY restrictions (prevent selling)</div>
                                      <div><span className="badge bg-danger">SELL ONLY</span> - SELL restrictions (prevent buying)</div>
                                      <div><span className="badge bg-secondary">HOLD</span> - HOLD restrictions (no trading)</div>
                                      <div><span className="badge bg-info text-white">WEIGHT</span> - Weight-based restrictions with min/max percentages</div>
                                    </div>

                                    <div className="d-flex flex-wrap gap-3 mb-3">
                                      <div><span className="badge bg-success">Client</span> - Client-imposed restrictions</div>
                                      <div><span className="badge bg-danger">Model</span> - Model-imposed restrictions</div>
                                      <div><span className="badge bg-secondary">Global</span> - Global security restrictions</div>
                                    </div>
                                    <div className="d-flex flex-wrap gap-3 mb-3">
                                      <div><span className="badge bg-success">Combined</span> - Client and Model restrictions combined</div>
                                      <div><span className="badge bg-info text-white">WEIGHT</span> - Weight-based restrictions with min/max percentages</div>
                                    </div>

                                    <h6 className="mb-2 text-primary">Additional Information:</h6>
                                    <div className="small text-muted">
                                      <p className="mb-1">• <strong>Restriction Note:</strong> Contains details about why the restriction was applied</p>
                                      <p className="mb-0">• <strong>Added By:</strong> Shows who created or last modified the restriction</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Trade Restrictions Loading State */}
                        {isLoadingTradeRestrictions ? (
                          <div className="d-flex justify-content-center my-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading trade restrictions...</span>
                            </div>
                            <span className="ms-2">Loading trade restrictions...</span>
                          </div>
                        ) : tradeRestrictionsError ? (
                          <div className="alert alert-danger">
                            <strong>Error:</strong> {tradeRestrictionsError}
                          </div>
                        ) : tradeRestrictionsData ? (
                          <div className="mt-4">
                            <SortableResizableTable
                              columns={tradeRestrictionsReportColumns}
                              data={tradeRestrictionsData}
                              hideDollarValues={hideDollarValues}
                              customRender={getTableConfig(tradeRestrictionsData).customRender}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* Securities Information Panel */}
                  <div className="card mb-4">
                    <div
                      className="card-header bg-info text-white d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={handleExpandSecuritiesInfo}
                    >
                      <h5 className="mb-0">
                        <i className={`bi ${isSecuritiesInfoCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
                        {' '}Securities Information
                      </h5>
                    </div>
                    <div className={`collapse ${!isSecuritiesInfoCollapsed ? 'show' : ''}`}>
                      <div className="card-body">
                        {/* Securities Information Guide - Collapsible */}
                        <div className="row mb-4">
                          <div className="col-md-12">
                            <div className="card h-100 bg-light">
                              <div
                                className="card-header bg-light d-flex justify-content-between align-items-center py-2"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setIsSecuritiesInfoGuideCollapsed(!isSecuritiesInfoGuideCollapsed)}
                              >
                                <h6 className="mb-0">
                                  <i className={`bi ${isSecuritiesInfoGuideCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
                                  {' '}Securities Information Guide
                                </h6>
                              </div>                              <div className={`collapse ${!isSecuritiesInfoGuideCollapsed ? 'show' : ''}`}>
                                <div className="card-body py-2">
                                  <div className="small">
                                    <p className="mb-2">This report shows securities with details about holdings, allocations, and trading recommendations across all accounts in the selected group for rebalancing purposes.</p>
                                    <h6 className="mb-2 text-primary">Trade Action with Restriction Color Guide:</h6>
                                    <div className="d-flex flex-wrap gap-3 mb-3">
                                      <div><span style={{ ...getTradeActionStyles('trade-action-buy'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>BUY</span> - Pure buy action (green)</div>
                                      <div><span style={{ ...getTradeActionStyles('trade-action-sell'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>SELL</span> - Pure sell action (red)</div>
                                      <div><span style={{ ...getTradeActionStyles('trade-action-hold'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>HOLD</span> - Hold action (gray)</div>
                                    </div>

                                    <div className="d-flex flex-wrap gap-3 mb-3">
                                      <div><span style={{ ...getTradeActionStyles('trade-action-buy-weight'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>BUY within Weight</span> - Buy with weight restrictions (light green)</div>
                                      <div><span style={{ ...getTradeActionStyles('trade-action-sell-weight'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>SELL within Weight</span> - Sell with weight restrictions (light red)</div>
                                    </div>
                                    <div className="d-flex flex-wrap gap-3 mb-3">
                                      <div><span style={{ ...getTradeActionStyles('trade-action-conflict'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>BUY/SELL but (...restriction)</span> - Conflicting restrictions (orange)</div>
                                      <div><span style={{ ...getTradeActionStyles('trade-action-unknown'), padding: '4px 8px', borderRadius: '4px', fontSize: '0.9rem' }}>Unknown</span> - Unknown actions (light gray)</div>
                                    </div>

                                    <h6 className="mb-2 text-primary">Trade Action Values:</h6>
                                    <div className="small text-muted">
                                      <p className="mb-1">• <strong>Pure Actions:</strong> "BUY", "SELL", "HOLD" - Simple trade recommendations based on target weights</p>
                                      <p className="mb-1">• <strong>Weight Restrictions:</strong> "BUY within Weight (X% - Y%)" or "SELL within Weight (X% - Y%)" - Actions with percentage constraints by the client, not the model</p>
                                      <p className="mb-1">• <strong>Conflicting Restrictions:</strong> "BUY but (SELL ONLY restriction)" - When model recommendation conflicts with client's or global restrictions</p>
                                      <p className="mb-0">• <strong>Empty/Null:</strong> No action required, typically for cash positions or unassigned securities</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Securities Data Loading State */}
                        {isLoadingSecurities ? (
                          <div className="d-flex justify-content-center my-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading securities information...</span>
                            </div>
                            <span className="ms-2">Loading securities information...</span>
                          </div>
                        ) : securitiesError ? (
                          <div className="alert alert-danger">
                            <strong>Error:</strong> {securitiesError}
                          </div>
                        ) : securitiesData ? (
                          <div className="mt-4">
                            <SortableResizableTable
                              columns={securitiesReportColumns}
                              data={securitiesData}
                              hideDollarValues={hideDollarValues}
                              customRender={getTableConfig(securitiesData).customRender}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {/* NEW Rebalance Panel */}
                  <div className="card mb-4">
                    <div
                      className="card-header bg-success text-white d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={handleExpandRebalance}
                    >
                      <h5 className="mb-0">
                        <i className={`bi ${isRebalanceCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
                        {' '}Rebalance
                      </h5>
                    </div>
                    <RebalancePanel
                      // Display preferences
                      hideDollarValues={hideDollarValues}

                      // API and data dependencies
                      selectedGroup={selectedGroup}

                      // Panel state
                      isRebalanceCollapsed={isRebalanceCollapsed}
                    />
                  </div>
                  {/* Detailed Information */}
                  <div className="card mb-4">
                    <div
                      className="card-header bg-primary text-white d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={handleExpandDetailed}
                    >
                      <h5 className="mb-0">
                        <i className={`bi ${isDetailedCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'}`}></i>
                        {' '}Detailed Information
                      </h5>
                    </div>
                    <div className={`collapse ${!isDetailedCollapsed ? 'show' : ''}`}>
                      <div className="card-body">
                        <div className="row mb-4">
                          <div className="col-md-4">
                            <label className="form-label">Reports:</label>
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              value={reportOptions.find(option => option.value === selectedReport)}
                              onChange={handleReportChange}
                              options={reportOptions}
                              isSearchable={false}
                              menuPortalTarget={document.body}
                              styles={{
                                control: (baseStyles, state) => ({
                                  ...baseStyles,
                                  borderColor: state.isFocused ? '#80bdff' : '#ced4da',
                                  boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : null,
                                  '&:hover': {
                                    borderColor: state.isFocused ? '#80bdff' : '#adb5bd'
                                  }
                                }),
                                menuPortal: (baseStyles) => ({
                                  ...baseStyles,
                                  zIndex: 10002
                                }),
                                menu: (baseStyles) => ({
                                  ...baseStyles,
                                  zIndex: 10002
                                }),
                                option: (baseStyles, state) => ({
                                  ...baseStyles,
                                  backgroundColor: state.isSelected
                                    ? '#007bff'
                                    : state.isFocused
                                      ? '#e9ecef'
                                      : null,
                                  color: state.isSelected ? 'white' : 'black',
                                })
                              }}
                            />
                          </div>
                          {/* Trade Actions Report Key - Only shown when Securities report is selected */}
                          {selectedReport === 'securities' && reportData && (
                            <div className="col-md-8">
                              <div className="card h-100 bg-light">
                                <div className="card-body py-2">
                                  <h6 className="card-title mb-2">Trade Actions Guide</h6>
                                  <div className="small">
                                    <p className="mb-2">This report shows securities and their recommended trading actions based on target weight thresholds.</p>
                                    <div className="d-flex flex-wrap gap-3">
                                      <div><span className="badge bg-success">Buy</span> - Security is below minimum target weight</div>
                                      <div><span className="badge bg-danger">Sell</span> - Security is above maximum target weight</div>
                                      <div><span className="badge bg-secondary">Hold</span> - Security is within target range</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Report Data Table */}
                        {reportData && (
                          <div className="mt-4">
                            <h6 className="mb-3">Report Data</h6>

                            {isLoadingReport ? (
                              <div className="d-flex justify-content-center my-4">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="visually-hidden">Loading report data...</span>
                                </div>
                                <span className="ms-2">Loading report data...</span>
                              </div>
                            ) : reportError ? (
                              <div className="alert alert-danger">
                                <strong>Error:</strong> {reportError}
                              </div>
                            ) : null}
                          </div>
                        )}

                        {/* Report Results Section */}
                        <>
                          {reportError ? (
                            <div className="alert alert-danger mt-4">
                              <strong>Error:</strong> {reportError}
                            </div>
                          ) : isLoadingReport ? (
                            <div className="d-flex justify-content-center mt-4">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading report...</span>
                              </div>
                              <span className="ms-2">Loading report...</span>
                            </div>
                          ) : reportData ? (
                            <div className="mt-4">
                              <SortableResizableTable
                                columns={
                                  selectedReport === 'accounts' ? accountReportColumns :
                                    securitiesReportColumns
                                }
                                data={reportData}
                                hideDollarValues={hideDollarValues}
                                customRender={getTableConfig(reportData).customRender}
                              />
                            </div>
                          ) : null}
                        </>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : rebalancingGroups.length > 0 ? (
            <div className="alert alert-info mt-4">
              Please select a rebalancing group to view its data.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Rebalance;
